import React from "react";
import { cn } from "@/lib/utils";

// Animated "chain of title" mark: root deed -> partition -> sale deed ->
// mutation -> verified shield. Namespaced classes + palette-mapped tokens so
// it composes cleanly inside panels. Honours prefers-reduced-motion.
const CSS = `
.cot-wrap{--ink:#20242A;--tint:#F7F5F1;--gold:#9C6F2A;--ok:#4F7A62}
.cot-wrap .cot-l{fill:none;stroke:var(--ink);stroke-width:.75;stroke-linecap:round;stroke-linejoin:round}
.cot-wrap .cot-p{fill:var(--tint)}
.cot-wrap text{font:12px "Cormorant Garamond",Georgia,serif;fill:var(--ink)}
.cot-wrap .cot-base{fill:none;stroke:var(--ink);stroke-width:.75;stroke-dasharray:3 3;opacity:.5}
.cot-wrap .cot-sg{fill:none;stroke:var(--gold);stroke-width:1.75;stroke-linecap:round;stroke-dasharray:1;stroke-dashoffset:0}
.cot-wrap .cot-nd{transform-box:fill-box;transform-origin:center}
.cot-wrap .cot-ck{fill:none;stroke:var(--ok);stroke-width:1.5;stroke-linecap:round;stroke-linejoin:round}
.cot-wrap .cot-sh{transform-box:fill-box;transform-origin:center}
.cot-wrap .cot-shc{fill:none;stroke:var(--ok);stroke-width:2;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:1;stroke-dashoffset:0}
.cot-wrap .cot-n1{animation:cot-n1 10s ease-out infinite}
.cot-wrap .cot-n2{animation:cot-n2 10s ease-out infinite}
.cot-wrap .cot-n3{animation:cot-n3 10s ease-out infinite}
.cot-wrap .cot-n4{animation:cot-n4 10s ease-out infinite}
.cot-wrap .cot-g1{animation:cot-g1 10s ease-in-out infinite}
.cot-wrap .cot-g2{animation:cot-g2 10s ease-in-out infinite}
.cot-wrap .cot-g3{animation:cot-g3 10s ease-in-out infinite}
.cot-wrap .cot-sh{animation:cot-sh 10s ease-out infinite}
.cot-wrap .cot-shc{animation:cot-sc 10s ease-out infinite}
@keyframes cot-n1{0%,2%{opacity:0;transform:scale(.4)}8%,94%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(.4)}}
@keyframes cot-n2{0%,18%{opacity:0;transform:scale(.4)}24%,94%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(.4)}}
@keyframes cot-n3{0%,36%{opacity:0;transform:scale(.4)}42%,94%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(.4)}}
@keyframes cot-n4{0%,54%{opacity:0;transform:scale(.4)}60%,94%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(.4)}}
@keyframes cot-g1{0%,8%{stroke-dashoffset:1}20%,94%{stroke-dashoffset:0}100%{stroke-dashoffset:1}}
@keyframes cot-g2{0%,26%{stroke-dashoffset:1}38%,94%{stroke-dashoffset:0}100%{stroke-dashoffset:1}}
@keyframes cot-g3{0%,44%{stroke-dashoffset:1}56%,94%{stroke-dashoffset:0}100%{stroke-dashoffset:1}}
@keyframes cot-sh{0%,64%{opacity:0;transform:translateY(8px)}72%,94%{opacity:1;transform:none}100%{opacity:0;transform:translateY(8px)}}
@keyframes cot-sc{0%,72%{stroke-dashoffset:1}80%,94%{stroke-dashoffset:0}100%{stroke-dashoffset:1}}
@media (prefers-reduced-motion:reduce){.cot-wrap *{animation:none!important}.cot-wrap .cot-nd,.cot-wrap .cot-sh{opacity:1!important;transform:none!important}.cot-wrap .cot-sg,.cot-wrap .cot-shc{stroke-dashoffset:0!important}}
`;

export function ChainOfTitleMark({ className, caption = false }) {
  return (
    <div className={cn("cot-wrap", className)} data-testid="chain-of-title-mark">
      <style>{CSS}</style>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300" role="img" aria-label="Chain of title verified from root deed to latest mutation" className="h-auto w-full">
        <path className="cot-base" d="M74 130 H139 M167 130 H233 M261 130 H326" />
        <path className="cot-sg cot-g1" pathLength="1" d="M74 130 H139" />
        <path className="cot-sg cot-g2" pathLength="1" d="M167 130 H233" />
        <path className="cot-sg cot-g3" pathLength="1" d="M261 130 H326" />
        <g className="cot-nd cot-n1"><circle className="cot-l cot-p" cx="60" cy="130" r="14" /><path className="cot-ck" d="M54 130 l4 4 l8 -8" /></g>
        <g className="cot-nd cot-n2"><circle className="cot-l cot-p" cx="153" cy="130" r="14" /><path className="cot-ck" d="M147 130 l4 4 l8 -8" /></g>
        <g className="cot-nd cot-n3"><circle className="cot-l cot-p" cx="247" cy="130" r="14" /><path className="cot-ck" d="M241 130 l4 4 l8 -8" /></g>
        <g className="cot-nd cot-n4"><circle className="cot-l cot-p" cx="340" cy="130" r="14" /><path className="cot-ck" d="M334 130 l4 4 l8 -8" /></g>
        <text x="60" y="164" textAnchor="middle">Root deed</text>
        <text x="153" y="164" textAnchor="middle">Partition</text>
        <text x="247" y="164" textAnchor="middle">Sale deed</text>
        <text x="340" y="164" textAnchor="middle">Mutation</text>
        <g className="cot-sh"><path className="cot-l cot-p" d="M200 194 l26 9 v20 c0 15 -11 24 -26 30 c-15 -6 -26 -15 -26 -30 v-20 z" /><path className="cot-shc" pathLength="1" d="M188 224 l9 9 l16 -18" /></g>
        {caption && <text x="200" y="284" textAnchor="middle">Title verified, root to branch</text>}
      </svg>
    </div>
  );
}
